import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { archiveMilestone, saveMilestone, setMilestoneEnabled } from '@/data/milestones';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { buildMilestoneDuplicatePayload } from '@/utils/milestones';

/**
 * Shared milestone mutations with consistent cache invalidation.
 *
 * Every write invalidates the definitions lists (both scopes), the touched
 * definition detail, and the org history + person badge queries — an
 * enable/archive changes what badges/progress are visible org-wide. The
 * loggedInUser query is deliberately NOT invalidated: milestone writes never
 * change profile data.
 */
const useMilestoneMutations = ({ organizationID } = {}) => {
    const queryClient = useQueryClient();

    const invalidate = (milestoneID = null) => {
        queryClient.invalidateQueries({ queryKey: ['milestones', organizationID] });
        if (milestoneID) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.milestones.definition(organizationID, milestoneID) });
        }
    };

    const save = useMutation({
        mutationFn: saveMilestone,
        onSuccess: (saved, variables) => {
            invalidate(saved?.id ?? null);
            Toast.show({ type: 'success', text2: variables?.definition?.id ? 'Milestone saved' : 'Milestone created' });
        },
    });

    const setEnabled = useMutation({
        mutationFn: setMilestoneEnabled,
        onSuccess: (saved, variables) => {
            invalidate(variables?.milestoneID ?? saved?.id ?? null);
            Toast.show({ type: 'success', text2: saved?.enabled ? 'Milestone enabled' : 'Milestone disabled' });
        },
    });

    const archive = useMutation({
        mutationFn: archiveMilestone,
        onSuccess: (result, variables) => {
            invalidate(variables?.milestoneID ?? result?.id ?? null);
            Toast.show({ type: 'success', text2: 'Milestone archived — earned badges stay visible' });
        },
    });

    const duplicate = useMutation({
        mutationFn: (definition) => saveMilestone({ definition: buildMilestoneDuplicatePayload(definition), expectedRevision: null }),
        onSuccess: (saved) => {
            invalidate(saved?.id ?? null);
            Toast.show({ type: 'success', text2: 'Duplicated as a new disabled milestone' });
        },
    });

    return { save, setEnabled, archive, duplicate };
};

export default useMilestoneMutations;
